import { checkText } from 'smile2emoji';
import * as utils from '../../../utils/utils.js';
import * as userPerms from '../../../utils/userPerms.js';

export default class QnAChat {
    /**
     * Creates an instance of QnAChat.
     *
     * @param {any} qnaService
     * @param {any} $ngRedux
     * @param {any} qnaActions
     *
     * @memberOf QnAChat
     */
    constructor($timeout, $window, uiService, watchService, $ngRedux, qnaActions) {
        this.$timeout = $timeout;
        this.$window = $window;
        this.service = uiService;
        this.watchService = watchService;
        this.store = $ngRedux;
        this.qnaActions = qnaActions;
        this.sendAnonymous = false;
        this.evtContactEmail = this.watchService.event.contactEmail;
        this.textBoxPlaceholder = 'Ask a question';
        this.user = utils.getCurrentUser();
        this.isUserAnonymous = userPerms.isAnonymous(this.user);
    }

    /**
     * When we initialize.
     *
     *
     * @memberOf QnAChat
     */
    $onInit() {
        console.log('QnA started');
        const actions = Object.assign({}, this.qnaActions);
        this.unsubscribe = this.store.connect(this.mapStateToThis, actions)(this);
        this.manageHub();
        this.isScrolling = false;
        if (
            this.watchService.event.userQuestionDescriptiveText !== null &&
            this.watchService.event.userQuestionDescriptiveText.length > 0
        )
            this.textBoxPlaceholder = this.watchService.event.userQuestionDescriptiveText;
    }

    /**
     * Maps state to 'this' to simplify access to
     * redux reducers.
     *
     * @param {any} state
     * @returns
     *
     * @memberOf QnAChat
     */
    mapStateToThis(state) {
        return {
            messages: state.messages,
            users: state.users,
            ui: state.ui
        };
    }

    /**
     * Contains all functions for SignalR.
     *
     *
     * @memberOf QnAChat
     */
    manageHub() {
        this.service.start();

        /**
         * Sets the connection ID.
         */
        this.service.on('updateConnectionId', id => {
            this.setConnectionId(id);
        });

        /**
         * Adds a new answer to the chat.
         */
        this.service.on('broadcastAnswer', message => {
            const answer = this.answerFactory(message);
            const questions = message.qnAQuestions.map(this.questionFactory);
            this.updateQuestions(questions);
            this.addMessages([answer]);
            this.signalNewAnswer();
        });

        /**
         * Adds the initial messages to the chat.
         */
        /* eslint-disable no-unused-vars */
        this.service.on(
            'broadcastInitialViewer',
            ({ chatMessages, questions, answers, announcements }) => {
                /* eslint-enable */
                const answersArr = answers.map(this.answerFactory);
                const questionsArr = questions.map(this.questionFactory);
                const messagesHistory = [...answersArr, ...questionsArr].sort(
                    (a, b) => new Date(a.timeStamp) - new Date(b.timeStamp)
                );
                this.setMessages(messagesHistory);
                this.handleScrollEvent();
                this.watchService.userInteractionResponsive = true;
                this.handleWindowResizeEvent();
            }
        );

        /**
         * Adds the user's question to the chat.
         */
        this.service.on('broadcastQuestionToChat', message => {
            const question = this.questionFactory(message);
            this.addMessages([question]);
            //this.scrollToBottom();
        });

        /**
         * Communicated to moderator group but not acted upon in chat.
         * Not defining them here leads to SignalR console warnings of method not found.
         */
        this.service.on('updateQuestions', () => {});
        this.service.on('broadcastInitialModerator', () => {});
        this.service.on('sendCurrentUserId', () => {});
        this.service.on('broadcastQuestion', () => {});
    }
    /**
     * Prevent message window from scrolling on new messages
     * if the user is currently scrolling
     */
    handleScrollEvent() {
        const el = document.querySelector('.vl-Qna-Chat-messages-container');
        angular.element(el).on('scroll', e => {
            this.isScrolling =
                e.target.scrollHeight - e.target.scrollTop - e.target.offsetHeight >= 10;
        });
    }

    answerFactory({ id, questionText, answerText, timeStamp, qnAQuestions }) {
        const type = {
            isAnswer: true
        };
        const timeConvertFunction = messageTime => {
            return utils.calculateDistanceFromTime(messageTime);
        };
        return Object.assign({}, type, {
            id: `a${id}`,
            questionText: checkText(utils.replaceNewline(questionText, '<br />')),
            answerText: checkText(utils.replaceNewline(answerText, '<br />')),
            timeStamp,
            time: timeConvertFunction(timeStamp),
            questionTime: timeConvertFunction(qnAQuestions[0].timeStamp)
        });
    }

    questionFactory({ id, timeStamp, message, userEmail, qnAAnswerId }) {
        const type = {
            isQuestion: true
        };
        const timeConvertFunction = messageTime => {
            return utils.calculateDistanceFromTime(messageTime);
        };
        return Object.assign({}, type, {
            id: `q${id}`,
            questionText: checkText(utils.replaceNewline(message, '<br />')),
            timeStamp,
            time: timeConvertFunction(timeStamp),
            upn: userEmail,
            isAnswered: qnAAnswerId !== null
        });
    }
    /**
     * Scrolls the chat to bottom on resize
     */
    handleWindowResizeEvent() {
        window.addEventListener('resize', () => {
            this.service.isScrolling = false;
        });
    }

    /**
     * Scroll the messages container to bottom
     */
    scrollToBottom() {
        const el = document.querySelector('.vl-Qna-Chat-messages-container');
        this.$timeout(
            () => {
                if (el && !this.isScrolling) {
                    el.scrollTop = el.scrollHeight;
                }
            },
            0,
            false
        );
    }

    /**
     * The message is sent to the backend, which broadcasts
     * it to all clients.
     */
    sendMessage() {
        if (!this.message) {
            return;
        }
        if (!this.message.text) {
            return;
        }
        this.isScrolling = false;
        const fnToInvoke = this.sendAnonymous ? 'AskQuestionAnon' : 'AskQuestion';

        this.service.invoke(fnToInvoke, [this.message.text.replace(/<[^>]*>/g, '')]);
        this.message.text = '';
    }

    isConnected() {
        return this.service.connectionStatus === this.service.SIGNALR_STATUS.CONNECTED;
    }

    isConnecting() {
        return this.service.connectionStatus === this.service.SIGNALR_STATUS.CONNECTING;
    }
}

QnAChat.$inject = ['$timeout', '$window', 'uiService', 'watchService', '$ngRedux', 'qnaActions'];
